import * as d3 from 'd3';
import { Defs, G, LinearGradient, Rect, Stop, Text, TextProps } from 'react-native-svg';
import { useDripsyTheme } from 'dripsy';
import React, { useEffect, useState } from 'react';

import { keyGenerator } from '@/utils/keyGenerator';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { AxisItem } from '../axis/useAxis.hook';
import { BarChartMargin } from './type';

interface BarsProps {
  xScale: d3.ScaleBand<string>;
  yScale: d3.ScaleLinear<number, number, never>;
  data: Array<AxisItem>;
  width: number;
  height: number;
  margin: BarChartMargin;
  barWidth?: number;
}

export const Bars: React.FC<BarsProps> = ({ xScale, yScale, data, width, height, margin, barWidth = 20 }) => {
  const graphWidth = width - margin.left * 4;
  const columnWidth = graphWidth / data.length;
  const columnPadding = columnWidth / 2;

  const transform = `translate(${margin.left * 2 + columnPadding}, ${height})`;
  const colours = ['#597EB9', '#264B86'];

  const { theme } = useDripsyTheme();

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <G transform={transform} width={width - margin.left * 2}>
      {data.map((item, index) => {
        const x = xScale(item.label) || 0;
        const y = yScale(0 - item.value) * -1;
        const barHeight = height - yScale(item.value);
        const key = keyGenerator(`bar-${index}`);
        let fill = index % 2 === 0 ? colours[0] : colours[1];
        if (hoveredIndex === index) {
          fill = theme.colors.$primary;
        }

        return (
          <G>
            <Defs>
              <LinearGradient id="barGradient" x1="0" y1="0" x2="1" y2="1">
                <Stop offset="0" stopColor="#6794C9" stopOpacity="1" />
                <Stop offset="1" stopColor="#305992" stopOpacity="1" />
              </LinearGradient>
              <LinearGradient id="barGradientHovered" x1="0" y1="0" x2="1" y2="1">
                <Stop offset="0" stopColor="#E7C76E" stopOpacity="1" />
                <Stop offset="1" stopColor="#A96823" stopOpacity="1" />
              </LinearGradient>
            </Defs>
            <Rect
              key={key}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              // fill={fill}
              fill={hoveredIndex === index ? 'url(#barGradientHovered)' : 'url(#barGradient)'}
              ry={barWidth / 2}
              onPress={() => console.log('@pressed')}
              onPressIn={() => console.log('@pressedIn')}
              onPressOut={() => console.log('@pressedOut')}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          </G>
        );
      })}
      {data.map((item, index) => {
        if (hoveredIndex !== index || !item.tooltipText) return null;
        const x = xScale(item.label) || 0;
        const y = yScale(0 - item.value) * -1;
        const barHeight = height - yScale(item.value);
        const key = keyGenerator(`bar-${index}`);

        // if index > 3, on the left side of the bar
        const tooltipOnLeftSide = index > data.length - 4;
        const xPos = tooltipOnLeftSide ? x - item.tooltipText.length * 8 : x - barWidth / 2 + 8;

        return (
          <G key={key}>
            {/* Padding 8px on Each SIDE */}
            <Rect
              x={xPos}
              y={y + barHeight / 3 - 12}
              // Depends on the length of the <Text> inside the <Rect>
              width={item.tooltipText.length * 8.2 + 16}
              height={64}
              fill={theme.colors.$white}
              rx={0}
              ry={0}
              // box shadow for the tooltip 3px 3px 6px rgba(0, 0, 0, 0.16)
              filter="drop-shadow(3px 3px 6px rgba(0, 0, 0, 0.16))"
              zIndex={1000 - index}
            />
            {/* Center aligned Text vertically and horizontally inside Rect */}
            <Text
              id={`tooltipText-${index}`}
              x={tooltipOnLeftSide ? x - item.tooltipText.length * 8 + 8 : x + barWidth / 2}
              y={y + barHeight / 3 + 8}
              textAnchor="start"
              alignmentBaseline="middle"
              fontSize={14}
              // font family: 'monospace'
              fontFamily="Poppins_400Regular"
              fontColor={theme.colors.$secondary}
              fill={theme.colors.$secondary}
              fontWeight="bold">
              {item.tooltipText}
            </Text>
            {/* Date */}
            <Text
              x={tooltipOnLeftSide ? x - item.tooltipText.length * 8 + 8 : x + barWidth / 2}
              y={y + barHeight / 3 + 25 + 8}
              textAnchor="start"
              alignmentBaseline="middle"
              fontSize={14}
              // font family: 'monospace'
              fontFamily="Poppins_400Regular"
              fontColor={theme.colors.$textSecondary}
              fill={theme.colors.$textSecondary}
              fontWeight="bold">
              {item.label}
            </Text>
          </G>
        );
      })}
    </G>
  );
};
